import React, { useState, forwardRef } from "react";

import FormField from "./Form.Field";
import FormLabel from "./Form.Label";
import FormHelp from "./Form.Help";
import FormWrapper from "./Form.Wrapper";
import FormValidation from "./Form.Validation";

import * as inputStyles from "./InputField.styles";
import * as styles from "./TextArea.styles";


export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  help?: string | React.ReactNode;
  label?: string | React.ReactNode;
  fieldProps?: Record<string, unknown>;
  isValid?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
}


const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({
  id,
  name,
  value,
  defaultValue,
  help,
  label,
  fieldProps = {},
  isValid = false,
  isInvalid = false,
  errorMessage,
  onFocus,
  onBlur,
  ...textareaProps
}, ref) => {

  const [ isFocused, setIsFocused ] = useState(false);
  const [ hasUncontrolledContent, setHasUncontrolledContent ] = useState(!!defaultValue);

  const isControlled = value !== undefined;
  const fieldId = id || `textarea-${name || "default"}`;
  const hasValueOrFocused = isControlled ? (!!value || isFocused) : (hasUncontrolledContent || isFocused);

  const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) {
      setHasUncontrolledContent(event.target.value.length > 0);
    }
    textareaProps.onChange?.(event);
  };

  return (
    <FormField
      className="field-textarea"
      {...fieldProps}>

      <FormWrapper
        floating={false}
        isInvalid={isInvalid}
        isValid={isValid}
        hasValueOrFocused={hasValueOrFocused}>

        <FormLabel htmlFor={fieldId}>
          {label}
        </FormLabel>

        <textarea
          ref={ref}
          id={fieldId}
          name={name}
          css={[
            inputStyles.base,
            styles.base,
            isInvalid && inputStyles.error,
            isValid && inputStyles.success
          ]}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          rows={3}
          {...(isControlled ? { value } : { defaultValue })}
          {...textareaProps} />

      </FormWrapper>

      <FormHelp>
        {help}
      </FormHelp>

      <FormValidation
        errorMessage={errorMessage || ""}
        isVisible={isInvalid} />

    </FormField>
  );
});

TextArea.displayName = "TextArea";

export default TextArea;
